import * as Yup from "yup";

/**
 * Creates a Yup validator for a single field based on its config.
 * - Handles dynamic fields, required, dependsOn, pattern, length, min/max, and custom logic.
 * - Supports select, text, number, date, checkbox, checkboxGroup, and custom dynamic fields.
 * - Integrates with Formik/Yup for robust form validation.
 * - Used for both simple and nested/dynamic form schemas.
 */
export const createFieldValidator = (field, formFields) => {
  let validator;

  // Handle dynamic field sets (e.g., array of grouped fields)
  if (field.dynamic && field.fields) {
    const fieldValidators = {};
    Object.entries(field.fields).forEach(([subFieldName, subFieldConfig]) => {
      fieldValidators[subFieldName] = Yup.array()
        .of(
          createSubFieldValidator({
            ...subFieldConfig,
            name: subFieldName,
          })
        )
        .min(1, `At least one ${field.title} is required`);
    });
    return Yup.object().shape(fieldValidators);
  }

  switch (field.type) {
    case "select":
      if (field.multiple) {
        validator = Yup.array()
          .nullable()
          .min(field.required ? 1 : 0, `${field.label} is required`);
      } else {
        validator = Yup.mixed().nullable();
      }
      break;
    case "text":
      validator = Yup.string()
        .nullable()
        .transform((value) => {
          if (value === undefined || value === null) return null;
          return value.trim() === "" ? null : value;
        })
        .test("not-empty", `${field.label} cannot be empty`, function(value) {
          // If field is not required and has no dependsOn, allow empty
          if (!field.required && !field.dependsOn) {
            return true;
          }

          // Handle dependsOn condition
          if (field.dependsOn) {
            const dependentValue = this.parent[field.dependsOn.field];
            const isRequired = field.dependsOn.condition === "equals" 
              ? dependentValue === field.dependsOn.value
              : Boolean(dependentValue);
            
            // If field is not required, allow empty regardless of dependsOn
            if (!field.required) {
              return true;
            }
            
            // If field is required and dependsOn condition is met, validate
            if (isRequired) {
              return value !== null && value !== undefined && value.trim() !== "";
            }
            
            // If dependsOn condition is not met, allow empty
            return true;
          }

          // For required fields without dependsOn
          if (field.required) {
            return value !== null && value !== undefined && value.trim() !== "";
          }

          return true;
        });
      if (field.validation?.pattern) {
        validator = validator.test("pattern", field.validation.message || `Invalid format`, function(value) {
          // If field is not required and has no dependsOn, allow empty
          if (!field.required && !field.dependsOn) {
            return true;
          }

          // Handle dependsOn condition
          if (field.dependsOn) {
            const dependentValue = this.parent[field.dependsOn.field];
            const isRequired = field.dependsOn.condition === "equals" 
              ? dependentValue === field.dependsOn.value
              : Boolean(dependentValue);
            
            // If field is not required, allow empty regardless of dependsOn
            if (!field.required) {
              return true;
            }
            
            // If field is required and dependsOn condition is met, validate pattern
            if (isRequired && value) {
              return new RegExp(field.validation.pattern).test(value);
            }
            
            // If dependsOn condition is not met, allow empty
            return true;
          }

          // For required fields without dependsOn
          if (field.required && value) {
            return new RegExp(field.validation.pattern).test(value);
          }

          return true;
        });
      }
      if (field.validation?.length) {
        validator = validator.test("length", `${field.label} must be exactly ${field.validation.length} characters`, function(value) {
          // If field is not required and has no dependsOn, allow empty
          if (!field.required && !field.dependsOn) {
            return true;
          }

          // Handle dependsOn condition
          if (field.dependsOn) {
            const dependentValue = this.parent[field.dependsOn.field];
            const isRequired = field.dependsOn.condition === "equals" 
              ? dependentValue === field.dependsOn.value
              : Boolean(dependentValue);
            
            // If field is not required, allow empty regardless of dependsOn
            if (!field.required) {
              return true;
            }
            
            // If field is required and dependsOn condition is met, validate length
            if (isRequired && value) {
              return value.length === field.validation.length;
            }
            
            // If dependsOn condition is not met, allow empty
            return true;
          }

          // For required fields without dependsOn
          if (field.required && value) {
            return value.length === field.validation.length;
          }

          return true;
        });
      }
      break;
    case "number":
      validator = Yup.number()
        .transform((value) => (isNaN(value) ? undefined : value))
        .nullable();
      if (field.validation?.min !== undefined) {
        validator = validator.min(field.validation.min);
      }
      if (field.validation?.max !== undefined) {
        validator = validator.max(field.validation.max);
      }
      break;
    case "date":
      validator = Yup.date()
        .transform((value) => (value === "" ? null : value))
        .nullable();
      if (field.validation?.min === "proposed_start_date") {
        validator = validator.test(
          "min-date",
          "End date must be after start date",
          function (value) {
            const startDate = this.parent.proposed_start_date;
            if (!startDate || !value) return true;
            return new Date(value) >= new Date(startDate);
          }
        );
      }
      break;
    case "checkbox":
      validator = Yup.boolean();
      break;
    case "checkboxGroup":
      validator = Yup.array()
        .nullable()
        .test(
          "checkbox-group-validation",
          `${
            field.label || "Checkbox group"
          } must have at least one valid selection`,
          (value) => {
            if (!Array.isArray(value)) return false; // Not an array
            if (value.length === 0) return false; // Empty array
            return value.some(
              (item) => item && Object.values(item).some((v) => v === true)
            );
          }
        );
      break;
    default:
      validator = Yup.string().nullable();
  }

  // Add required/dependsOn logic at the end to ensure all tests are wrapped
  if (field.dependsOn) {
    validator = validator.when(field.dependsOn.field, {
      is: (val) => {
        if (field.dependsOn.condition === "equals") {
          return val === field.dependsOn.value;
        }
        return Boolean(val);
      },
      then: (schema) => {
        if (field.required) {
          return schema.required(`${field.label} is required`);
        }
        return schema;
      },
      otherwise: (schema) => schema.nullable(),
    });
  } else if (field.required) {
    validator = validator.required(`${field.label} is required`);
  }

  return validator;
};

/**
 * Creates a Yup validator for a subfield (used in dynamic field sets).
 * - Handles text, email, tel, and default string types.
 * - Supports required, min/max length, and custom validation logic.
 * - Used for validating array/grouped fields (e.g., contacts).
 */
const createSubFieldValidator = (fieldConfig) => {
  let validator;

  switch (fieldConfig.type) {
    case "text":
      validator = Yup.string().nullable();

      if (fieldConfig.required || fieldConfig.validation) {
        validator = validator.test({
          name: "text-validation",
          test: function (value) {
            if (!value && !fieldConfig.required) {
              return true;
            }

            if (value) {
              const minLength = fieldConfig.validation?.min || 2;
              const maxLength = fieldConfig.validation?.max || 50;

              if (value.length < minLength) {
                return this.createError({
                  message: `${fieldConfig.label} must be at least ${minLength} characters`,
                });
              }

              if (value.length > maxLength) {
                return this.createError({
                  message: `${fieldConfig.label} cannot exceed ${maxLength} characters`,
                });
              }
            }

            return true;
          },
        });
      }
      break;
    case "email":
      validator = Yup.string()
        .trim()
        .test(
          "valid-email",
          "Please enter a valid email address with a proper domain",
          (value) => {
            if (!value) return true;

            const isValidFormat =
              /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(value);
            const parts = value.split("@");
            if (parts.length !== 2) return false;

            const domain = parts[1];
            const hasDot = domain.includes(".");
            const tldLength = domain.split(".").pop().length;

            return isValidFormat && hasDot && tldLength >= 2;
          }
        )
        .required(`${fieldConfig.label} is required`);
      break;
    case "tel":
      validator = Yup.string()
        .trim()
        .test("phone-validation", "Phone number must be exactly 10 digits", (value) => {
          if (!value && !fieldConfig.required) {
            return true;
          }
          return !value || /^[0-9]{10}$/.test(value);
        });
      break;
    default:
      validator = Yup.string()
        .trim()
        .required(`${fieldConfig.label} is required`);
  }
  if (fieldConfig.required) {
    validator = validator.required(`${fieldConfig.label} is required`);
  } else {
    validator = validator.nullable(); // Make field optional if not required
  }

  return validator;
};
